import { Box, MenuItem, MenuList, Modal, Typography } from "@mui/material";
import { hasAnyAccessToResource } from "api/general";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { Search } from "components/FormControls/Select/Search";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import {
  selectPopularConditions,
  setPopularConditions as setPopularConditionsStore,
} from "reduxState/campaigns/campaigns";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  CampaignCondition,
  CampaignConditionNewFormat,
  CampaignTriggersUnion,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import { findConditionIdPath, isNewFormat } from "../../../campaignHelpers";
import { useStyles } from "./CommandBar.styles";
import {
  defaultPopularConditions,
  filterTriggerBasedConditions,
  getMemberConditions,
  popularConditionsTriggerValue,
} from "./helpers";

const tabs = {
  popularConditions: "popularConditions",
  memberDetails: "memberDetails",
  triggerBasedConditions: "triggerBasedConditions",
  transactionFilters: "transactionFilters",
} as const;

type TabLabel = (typeof tabs)[keyof typeof tabs];

const tabsList = Object.keys(tabs) as TabLabel[];

interface Props {
  conditions: (
    | CampaignCondition
    | SelectedSchemaCondition
    | CampaignConditionNewFormat
  )[];
  isOpen: boolean;
  onClose: () => void;
  unshift: (value: any) => void;
  isRefferalCampaign: boolean;
  trigger?: CampaignTriggersUnion;
  transactionItemsFilters?: TransactionItem[];
}

export type ConditionWithCounter =
  | (CampaignCondition & { counter: number })
  | (SelectedSchemaCondition & { counter: number })
  | (CampaignConditionNewFormat & { counter: number });

export const CommandBar = ({
  conditions,
  isOpen,
  onClose,
  unshift,
  isRefferalCampaign,
  trigger,
  transactionItemsFilters,
}: Props) => {
  const { message } = useMessage();
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");
  const areTransactionItemsFilters =
    transactionItemsFilters && transactionItemsFilters.length > 0;

  const filteredPopularConditions: ConditionWithCounter[] =
    !hasAnyChannelPermission
      ? defaultPopularConditions.filter(
          (condition) =>
            findConditionIdPath(condition) !== "transaction.channelId",
        )
      : !hasAnyTierPermission
        ? defaultPopularConditions.filter(
            (condition) =>
              findConditionIdPath(condition) !== "customer.levelId",
          )
        : defaultPopularConditions;

  const savedPopularConditions = useAppSelector(selectPopularConditions);

  const filteredSavedPopularConditions = useMemo(() => {
    return areTransactionItemsFilters
      ? savedPopularConditions
      : savedPopularConditions.filter(
          (item) => !findConditionIdPath(item)?.includes("transaction.items"),
        );
  }, [areTransactionItemsFilters, savedPopularConditions]);

  const storedPopularConditions: ConditionWithCounter[] =
    savedPopularConditions.length > 0
      ? filteredSavedPopularConditions
      : filteredPopularConditions;

  const memberDetailsConditions = getMemberConditions(
    conditions,
    isRefferalCampaign,
    hasAnyTierPermission,
  );

  const triggerBasedConditions = filterTriggerBasedConditions(
    !hasAnyChannelPermission,
    conditions.filter(
      (condition) =>
        !(condition as CampaignConditionNewFormat)?.value?.field.includes(
          "transaction.items.",
        ),
    ),
    memberDetailsConditions as ConditionWithCounter[],
  );

  const transactionFiltersWithCounter = conditions
    .filter((condition) =>
      (condition as CampaignConditionNewFormat)?.value?.field.includes(
        "transaction.items",
      ),
    )
    .map((condition) => ({ ...condition, counter: 0 }));

  const allConditions = memberDetailsConditions.concat(
    [
      {
        attribute: "expression",
        label: "expression",
        operator: "expression",
        type: "expression",
        counter: popularConditionsTriggerValue,
      },
    ] as ConditionWithCounter[],
    triggerBasedConditions,
    areTransactionItemsFilters ? transactionFiltersWithCounter : [],
  );

  const getSearchedConditions = (conditionsArray: ConditionWithCounter[]) => {
    return conditionsArray.filter((condition) => {
      const label = message(
        { id: condition.label },
        {
          walletName: defaultWallet?.name,
          units: defaultWallet?.unitPluralName,
          currency,
        },
      );

      return label?.toLowerCase().includes(searchQuery?.toLowerCase());
    });
  };

  const [activeTab, setActiveTab] = useState<TabLabel | false>(
    tabs.popularConditions,
  );
  const [searchedValue, setSearchedValue] = useState("");
  const [popularConditions, setPopularConditions] = useState<
    ConditionWithCounter[]
  >(storedPopularConditions);
  const { debouncedValue: searchQuery, isTyping } = useDebounce(searchedValue);

  const findTriggerBasedLabel = (
    storedPopularConditions: ConditionWithCounter[],
  ) => {
    return storedPopularConditions.map((storedCondition) => {
      const isNewFormatCondtion = isNewFormat(storedCondition);
      const storedValueToCompare = isNewFormatCondtion
        ? storedCondition.value.field
        : storedCondition.attribute;

      return {
        ...storedCondition,
        label:
          conditions.find((condition) => {
            const valueToComapre = isNewFormat(condition)
              ? condition.value.field
              : condition.attribute;

            return storedValueToCompare === valueToComapre;
          })?.label || storedCondition.label,
      };
    });
  };

  useEffect(() => {
    setPopularConditions(findTriggerBasedLabel(storedPopularConditions));
    // eslint-disable-next-line
  }, [storedPopularConditions]);

  const handleConditionClick = (item: ConditionWithCounter) => {
    const conditionWithoutLabel = { ...item };

    // REMOVE LABELINFO KEY AS ITS NOT SERIALIZABLE
    if ("labelInfo" in conditionWithoutLabel) {
      delete conditionWithoutLabel.labelInfo;
    }
    unshift({
      draft: { ...conditionWithoutLabel, counter: undefined },
      isEditing: true,
      isSaved: false,
    });

    const existingCondition = popularConditions.find(
      (condition) =>
        findConditionIdPath(condition) === findConditionIdPath(item),
    );

    if (existingCondition) {
      const updatedConditions = popularConditions.map((condition) =>
        findConditionIdPath(condition) === findConditionIdPath(item)
          ? { ...condition, counter: condition.counter + 1 }
          : condition,
      );
      const updatedItem = updatedConditions.find(
        (condition) =>
          findConditionIdPath(condition) ===
          findConditionIdPath(existingCondition),
      );

      if (
        updatedItem &&
        updatedItem.counter === popularConditionsTriggerValue
      ) {
        setPopularConditions([
          updatedItem,
          ...popularConditions.filter(
            (condition) =>
              findConditionIdPath(condition) !==
              findConditionIdPath(updatedItem),
          ),
        ]);
        dispatch(
          setPopularConditionsStore({
            popularConditions: [
              updatedItem,
              ...popularConditions.filter(
                (condition) =>
                  findConditionIdPath(condition) !==
                  findConditionIdPath(updatedItem),
              ),
            ],
          }),
        );
      } else {
        setPopularConditions(updatedConditions);
        dispatch(
          setPopularConditionsStore({
            popularConditions: updatedConditions,
          }),
        );
      }
    } else {
      dispatch(
        setPopularConditionsStore({
          popularConditions: [
            ...popularConditions,
            { ...conditionWithoutLabel, counter: 1 },
          ],
        }),
      );
      setPopularConditions([
        ...popularConditions,
        { ...conditionWithoutLabel, counter: 1 },
      ]);
    }

    setActiveTab(tabs.popularConditions);
    setSearchedValue("");
    onClose();
  };

  const createConditionMenuItem = (item: ConditionWithCounter) => {
    const key = findConditionIdPath(item);

    return (
      <MenuItem
        className={classes.menuItem}
        key={key}
        onClick={() => handleConditionClick(item)}
      >
        {message(
          {
            id: item.label,
          },
          {
            walletName: defaultWallet?.name,
            units: defaultWallet?.unitPluralName,
            currency,
          },
        )}
        {"labelInfo" in item && (
          <span className={classes.labelInfo}>{item.labelInfo}</span>
        )}
      </MenuItem>
    );
  };
  const isTimeTriggerCampaign = trigger === "time";

  return (
    <Modal
      className={classes.modal}
      onClose={() => {
        setActiveTab(tabs.popularConditions);
        setSearchedValue("");
        onClose();
      }}
      open={isOpen}
    >
      <Box className={classes.modalContent}>
        <Box className={classes.searchAndCloseWrapper}>
          <Search
            className={classes.search}
            progressClassName={classes.loader}
            placeholder={message({ id: "searchForConditions" })}
            searchedValue={searchedValue}
            setSearchedValue={setSearchedValue}
            isLoading={isTyping}
            onInputClick={() => setActiveTab(false)}
          />
          <OLClearIcon
            onClick={() => {
              setActiveTab(tabs.popularConditions);
              setSearchedValue("");
              onClose();
            }}
          />
        </Box>
        <Typography className={classes.title}>
          {message({ id: "categories" })}
        </Typography>
        <OLTabs
          className={classes.marginLeft}
          items={
            isTimeTriggerCampaign
              ? tabsList.filter(
                  (tab) =>
                    tab !== tabs.triggerBasedConditions &&
                    tab !== tabs.transactionFilters,
                )
              : !areTransactionItemsFilters
                ? tabsList.filter((tab) => tab !== tabs.transactionFilters)
                : tabsList
          }
          value={activeTab}
          onChange={(_: React.SyntheticEvent, newValue: TabLabel) => {
            setSearchedValue("");
            setActiveTab(newValue);
          }}
          data-test-id="tabs"
        />
        <MenuList className={classes.list} role="listbox">
          {activeTab === tabs.popularConditions
            ? getSearchedConditions(popularConditions).map(
                (item) =>
                  item.counter >= popularConditionsTriggerValue &&
                  conditions.find(
                    (condition) =>
                      findConditionIdPath(condition) ===
                      findConditionIdPath(item),
                  ) &&
                  createConditionMenuItem(item),
              )
            : activeTab === tabs.memberDetails
              ? getSearchedConditions(
                  memberDetailsConditions as ConditionWithCounter[],
                ).map((item) => createConditionMenuItem(item))
              : activeTab === tabs.triggerBasedConditions
                ? getSearchedConditions(
                    triggerBasedConditions as ConditionWithCounter[],
                  ).map((item) => createConditionMenuItem(item))
                : activeTab === tabs.transactionFilters
                  ? getSearchedConditions(transactionFiltersWithCounter).map(
                      (item) => createConditionMenuItem(item),
                    )
                  : getSearchedConditions(
                      allConditions as ConditionWithCounter[],
                    ).map((item) => createConditionMenuItem(item))}
        </MenuList>
      </Box>
    </Modal>
  );
};
